import { type FormEvent, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { TextField } from '@/ui/Field/Field'
import { Stack } from '@/ui/layout/Stack'
import { resetPassword } from '../api/auth'
import { AuthLink, AuthShell } from '../components/AuthShell'
import { ROUTES } from '../constants/routes'
import { useAsyncSubmit } from '../hooks/useAsyncSubmit'

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const { loading, error, setError, submit } = useAsyncSubmit()
  const [fieldError, setFieldError] = useState<{
    field: 'password' | 'confirm'
    message: string
  } | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setFieldError(null)
    setError(null)
    if (password !== confirm) {
      setFieldError({ field: 'confirm', message: authCopy.passwordMismatch })
      return
    }
    if (password.length < 8) {
      setFieldError({
        field: 'password',
        message: authCopy.passwordTooShort,
      })
      return
    }
    await submit(async () => {
      await resetPassword(token, password)
      void navigate(ROUTES.login, {
        replace: true,
        state: { message: authCopy.reset.done },
      })
    }, authCopy.reset.failed)
  }

  if (!token) {
    return (
      <AuthShell>
        <Stack gap="space-16">
          <Alert tone="danger">{authCopy.reset.missingToken}</Alert>
          <AuthLink href={ROUTES.forgotPassword}>
            {authCopy.reset.requestNewLink}
          </AuthLink>
        </Stack>
      </AuthShell>
    )
  }

  return (
    <AuthShell heading={authCopy.reset.heading}>
      <Stack as="form" gap="space-16" onSubmit={(e) => void handleSubmit(e)}>
        <TextField
          id="password"
          label={authCopy.reset.newPassword}
          value={password}
          onValue={setPassword}
          error={
            fieldError?.field === 'password' ? fieldError.message : undefined
          }
          inputProps={{
            type: 'password',
            autoComplete: 'new-password',
            required: true,
          }}
        />
        <TextField
          id="confirm"
          label={authCopy.confirmPassword}
          value={confirm}
          onValue={setConfirm}
          error={
            fieldError?.field === 'confirm' ? fieldError.message : undefined
          }
          inputProps={{
            type: 'password',
            autoComplete: 'new-password',
            required: true,
          }}
        />
        {error && <Alert tone="danger">{error}</Alert>}
        <Button type="submit" variant="primary" loading={loading}>
          {authCopy.reset.submit}
        </Button>
      </Stack>
    </AuthShell>
  )
}
